import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileTextIcon, DownloadIcon, CopyIcon, CheckIcon } from 'lucide-react';
interface SurveyData {
  dealFocus: string;
  budget: string;
  timeline: string;
  firstTimeInvestor: string;
  importantFactors: string[];
  preferredArea: string;
  additionalContext: string;
}
export function DealReport() {
  const [surveyData, setSurveyData] = useState<SurveyData>({
    dealFocus: '',
    budget: '',
    timeline: '',
    firstTimeInvestor: '',
    importantFactors: [],
    preferredArea: '',
    additionalContext: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [investorId, setInvestorId] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const generateInvestorId = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let id = 'CBF-';
    for (let i = 0; i < 4; i++) {
      id += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return id;
  };
  const handleCheckboxChange = (factor: string) => {
    setSurveyData(prev => ({
      ...prev,
      importantFactors: prev.importantFactors.includes(factor) ? prev.importantFactors.filter(f => f !== factor) : [...prev.importantFactors, factor]
    }));
  };
  const validateForm = () => {
    const newErrors: string[] = [];
    if (!surveyData.dealFocus) newErrors.push('Please select your deal focus');
    if (!surveyData.budget) newErrors.push('Please select your budget range');
    if (!surveyData.timeline) newErrors.push('Please select your investment timeline');
    if (!surveyData.firstTimeInvestor) newErrors.push("Please indicate if you're a first-time Cabo investor");
    if (surveyData.importantFactors.length === 0) newErrors.push('Please select at least one important factor');
    if (!surveyData.preferredArea) newErrors.push('Please select your preferred area');
    setErrors(newErrors);
    return newErrors.length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    const id = generateInvestorId();
    setInvestorId(id);
    setIsSubmitted(true);
    console.log('Survey Data:', surveyData);
    console.log('Investor ID:', id);
  };
  const copyToClipboard = () => {
    navigator.clipboard.writeText(investorId);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };
  if (isSubmitted) {
    return <section id="deal-report" className="py-24 bg-charcoal" data-id="element-134">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-135">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5
        }} className="bg-navy rounded-lg p-8 sm:p-12 border border-gold" data-id="element-136">
            <div className="text-center mb-8" data-id="element-137">
              <CheckIcon className="text-gold mx-auto mb-4" size={64} data-id="element-138" />
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white mb-4" data-id="element-139">
                Survey Complete
              </h2>
              <p className="text-white/80 text-lg" data-id="element-140">
                Your Investor ID has been generated. Save this for future
                reference.
              </p>
            </div>

            <div className="bg-charcoal rounded-lg p-6 mb-8" data-id="element-141">
              <p className="text-white/60 text-sm mb-2" data-id="element-142">Your Investor ID</p>
              <div className="flex items-center justify-between bg-navy/50 rounded px-4 py-3 border border-gold/30" data-id="element-143">
                <span className="text-gold font-mono text-2xl font-bold" data-id="element-144">
                  {investorId}
                </span>
                <button onClick={copyToClipboard} className="text-gold hover:text-gold-light transition-colors" title="Copy to clipboard" data-id="element-145">
                  {isCopied ? <CheckIcon size={24} data-id="element-146" /> : <CopyIcon size={24} data-id="element-147" />}
                </button>
              </div>
            </div>

            <div className="bg-gold/10 border border-gold/30 rounded-lg p-6 mb-8" data-id="element-148">
              <h3 className="text-gold font-semibold mb-3 flex items-center" data-id="element-149">
                <FileTextIcon className="mr-2" size={20} data-id="element-150" />
                Important: Save Your Investor ID
              </h3>
              <ul className="text-white/80 space-y-2 text-sm" data-id="element-151">
                <li data-id="element-152">
                  • Screenshot or write down your Investor ID:{' '}
                  <span className="text-gold font-mono font-semibold" data-id="element-153">
                    {investorId}
                  </span>
                </li>
                <li data-id="element-154">• If you ever contact us, reference this ID</li>
                <li data-id="element-155">
                  • We'll pull exactly what you told us you're looking for — no
                  need to start over
                </li>
                <li data-id="element-156">
                  • This ID is unique to your preferences and investment
                  criteria
                </li>
              </ul>
            </div>

            <button onClick={() => alert('Deal Report PDF download initiated')} className="w-full bg-gold hover:bg-gold-light text-navy px-8 py-4 rounded-lg font-bold text-lg transition-colors flex items-center justify-center gap-3" data-id="element-157">
              <DownloadIcon size={24} data-id="element-158" />
              Download Deal Report
            </button>

            <p className="text-white/60 text-center text-sm mt-6" data-id="element-159">
              The report contains full investment analysis, financial models,
              and ongoing deal rankings.
            </p>
          </motion.div>
        </div>
      </section>;
  }
  return <section id="deal-report" className="py-24 bg-charcoal" data-id="element-160">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-161">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-12" data-id="element-162">
          <FileTextIcon className="text-gold mx-auto mb-4" size={48} data-id="element-163" />
          <h2 className="text-4xl sm:text-5xl font-serif font-bold text-white mb-6" data-id="element-164">
            Get the Full Cabo Deal Flow Report
          </h2>
          <p className="text-xl text-white/80 max-w-3xl mx-auto mb-4" data-id="element-165">
            This report contains full investment analysis, financial models, and
            ongoing deal rankings — updated as the market shifts.
          </p>
          <p className="text-white/60 max-w-2xl mx-auto" data-id="element-166">
            To access it, complete a short investor survey (7 questions, ~3
            minutes). The survey allows us to continuously improve the quality
            of deal flow we present. When we know what the market is looking
            for, we source better deals.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="bg-navy rounded-lg p-8 sm:p-10 border border-white/10" data-id="element-167">
          <div className="bg-gold/10 border border-gold/30 rounded-lg p-4 mb-8" data-id="element-168">
            <p className="text-gold text-sm font-semibold" data-id="element-169">
              ✓ We do NOT ask for your name or phone number. The survey is
              anonymous unless you choose to share contact info.
            </p>
          </div>

          {errors.length > 0 && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6" data-id="element-170">
              <p className="text-red-400 font-semibold mb-2" data-id="element-171">
                Please complete the following:
              </p>
              <ul className="text-red-300 text-sm space-y-1" data-id="element-172">
                {errors.map((error, index) => <li key={index} data-id="element-173">• {error}</li>)}
              </ul>
            </div>}

          <form onSubmit={handleSubmit} className="space-y-8" data-id="element-174">
            {/* Deal Focus */}
            <div data-id="element-175">
              <label className="block text-white font-semibold mb-3" data-id="element-176">
                1. What type of deal are you primarily focused on?
              </label>
              <div className="space-y-2" data-id="element-177">
                {['Cash Flow', 'Equity Appreciation', 'Land', 'Commercial', 'All of the Above'].map(option => <label key={option} className="flex items-center text-white/80 hover:text-white cursor-pointer" data-id="element-178">
                    <input type="radio" name="dealFocus" value={option} checked={surveyData.dealFocus === option} onChange={e => setSurveyData({
                  ...surveyData,
                  dealFocus: e.target.value
                })} className="mr-3 w-4 h-4 text-gold" data-id="element-179" />
                    {option}
                  </label>)}
              </div>
            </div>

            {/* Budget Range */}
            <div data-id="element-180">
              <label className="block text-white font-semibold mb-3" data-id="element-181">
                2. What is your investment budget range?
              </label>
              <div className="space-y-2" data-id="element-182">
                {['$250K - $500K', '$500K - $1M', '$1M - $3M', '$3M+'].map(option => <label key={option} className="flex items-center text-white/80 hover:text-white cursor-pointer" data-id="element-183">
                      <input type="radio" name="budget" value={option} checked={surveyData.budget === option} onChange={e => setSurveyData({
                  ...surveyData,
                  budget: e.target.value
                })} className="mr-3 w-4 h-4 text-gold" data-id="element-184" />
                      {option}
                    </label>)}
              </div>
            </div>

            {/* Timeline */}
            <div data-id="element-185">
              <label className="block text-white font-semibold mb-3" data-id="element-186">
                3. What is your investment timeline?
              </label>
              <div className="space-y-2" data-id="element-187">
                {['Ready Now', '3-6 Months', '6-12 Months', '12+ Months'].map(option => <label key={option} className="flex items-center text-white/80 hover:text-white cursor-pointer" data-id="element-188">
                      <input type="radio" name="timeline" value={option} checked={surveyData.timeline === option} onChange={e => setSurveyData({
                  ...surveyData,
                  timeline: e.target.value
                })} className="mr-3 w-4 h-4 text-gold" data-id="element-189" />
                      {option}
                    </label>)}
              </div>
            </div>

            {/* First-time Investor */}
            <div data-id="element-190">
              <label className="block text-white font-semibold mb-3" data-id="element-191">
                4. Are you a first-time Cabo investor or have you invested in
                Cabo before?
              </label>
              <div className="space-y-2" data-id="element-192">
                {['First-time Cabo investor', 'Have invested in Cabo before'].map(option => <label key={option} className="flex items-center text-white/80 hover:text-white cursor-pointer" data-id="element-193">
                    <input type="radio" name="firstTimeInvestor" value={option} checked={surveyData.firstTimeInvestor === option} onChange={e => setSurveyData({
                  ...surveyData,
                  firstTimeInvestor: e.target.value
                })} className="mr-3 w-4 h-4 text-gold" data-id="element-194" />
                    {option}
                  </label>)}
              </div>
            </div>

            {/* Important Factors */}
            <div data-id="element-195">
              <label className="block text-white font-semibold mb-3" data-id="element-196">
                5. What is most important to you? (Select all that apply)
              </label>
              <div className="space-y-2" data-id="element-197">
                {['Cash Flow', 'Equity Upside', 'Long-term Hold', 'Short-term Flip', 'Land Banking', 'Commercial'].map(factor => <label key={factor} className="flex items-center text-white/80 hover:text-white cursor-pointer" data-id="element-198">
                    <input type="checkbox" checked={surveyData.importantFactors.includes(factor)} onChange={() => handleCheckboxChange(factor)} className="mr-3 w-4 h-4 text-gold rounded" data-id="element-199" />
                    {factor}
                  </label>)}
              </div>
            </div>

            {/* Preferred Area */}
            <div data-id="element-200">
              <label className="block text-white font-semibold mb-3" data-id="element-201">
                6. Is there a specific area of Cabo you prefer?
              </label>
              <select value={surveyData.preferredArea} onChange={e => setSurveyData({
              ...surveyData,
              preferredArea: e.target.value
            })} className="w-full bg-charcoal text-white border border-white/20 rounded-lg px-4 py-3 focus:outline-none focus:border-gold" data-id="element-202">
                <option value="" data-id="element-203">Select an area...</option>
                <option value="Marina" data-id="element-204">Marina</option>
                <option value="Corridor" data-id="element-205">Corridor</option>
                <option value="East Cape" data-id="element-206">East Cape</option>
                <option value="Pedregal" data-id="element-207">Pedregal</option>
                <option value="Pacific Side" data-id="element-208">Pacific Side</option>
                <option value="Open to All" data-id="element-209">Open to All</option>
              </select>
            </div>

            {/* Additional Context */}
            <div data-id="element-210">
              <label className="block text-white font-semibold mb-3" data-id="element-211">
                7. Any additional context about what you're looking for?
                (Optional)
              </label>
              <textarea value={surveyData.additionalContext} onChange={e => setSurveyData({
              ...surveyData,
              additionalContext: e.target.value
            })} rows={4} className="w-full bg-charcoal text-white border border-white/20 rounded-lg px-4 py-3 focus:outline-none focus:border-gold resize-none" placeholder="Share any specific requirements, preferences, or questions..." data-id="element-212" />
            </div>

            <button type="submit" className="w-full bg-gold hover:bg-gold-light text-navy px-8 py-4 rounded-lg font-bold text-lg transition-colors" data-id="element-213">
              Complete Survey & Get Deal Report
            </button>
          </form>
        </motion.div>
      </div>
    </section>;
}